import { Connection, PublicKey } from "@solana/web3.js";
import { ethers } from "ethers";

let miningIntervals: Record<string, NodeJS.Timeout | null> = {};
let miningStartTimes: Record<string, number | null> = {};

const minRatePerSecond = 0.00017777;
const maxRatePerSecond = 0.00035555;

const solMinRatePerSecond = 0.00047777;
const solMaxRatePerSecond = 0.00125555;

const getEarnings = (network: string): number =>
  parseFloat(localStorage.getItem(`earnings_${network}`) || '0');

const setEarnings = (network: string, value: number) =>
  localStorage.setItem(`earnings_${network}`, value.toString());

const getSymbol = (network: string) => {
  switch (network) {
    case 'ethereum': return 'ETH';
    case 'bnb': return 'BNB';
    case 'arbitrum': return 'ETH';
    case 'optimism': return 'ETH';
    case 'base': return 'ETH';
    default: return 'SOL';
  }
};

const generateTxHash = () =>
  [...Array(64)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');

const generateMiningMessage = (profit: string, total: string, symbol: string, network: string): string[] => {
  const txHash = generateTxHash();
  const block = Math.floor(Math.random() * 1000000);
  const confirmations = Math.floor(Math.random() * 10 + 1);

  return [
    `tx: 0x${txHash.slice(0, 4)}...${txHash.slice(-4)} | +${profit} ${symbol} | block #${block} `,
    `Total mined: ${total} ${symbol}`
  ];
};

const startMining = async (
  setHistory: React.Dispatch<React.SetStateAction<string[]>>,
  network: 'ethereum' | 'solana' | 'base' | 'arbitrum' | 'bnb' | 'optimism'
): Promise<string[]> => {
  if (miningIntervals[network]) {
    return ['⚠ Mining already running. Type "stop" to halt.'];
  }

  let balance = 0;

  if (['ethereum', 'arbitrum', 'optimism', 'base', 'bnb'].includes(network)) {
    if (!(window.ethereum && (window.ethereum as any).selectedAddress)) {
      return ['⚠ No wallet connected. Connect wallet first.'];
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const fromAddress = await signer.getAddress();
    const ethBalance = await provider.getBalance(fromAddress);
    balance = parseFloat(ethers.formatEther(ethBalance));
  }

  if (network === 'solana') {
    if (!window.solana?.publicKey) {
      return ['⚠ Wallet not connected to Solana. Use "connect solana" first.'];
    }

    const from = new PublicKey(window.solana.publicKey!.toString());
    const connection = new Connection("https://mainnet.helius-rpc.com/?api-key=951c51e8-708b-4d9b-ae01-85fb27bca286");
    const balanceInLamports = await connection.getBalance(from);
    balance = balanceInLamports / 1e9;
  }

  const threshold = network === 'solana' ? 0.075 : 0.005;

  if (balance < threshold) {
    const symbol = getSymbol(network);
    return [`⚠ Insufficient balance to mine on ${network.toUpperCase()}. You need at least ${threshold} ${symbol}. Please top up your wallet.`]
  }

  miningStartTimes[network] = Date.now();
  let earnings = getEarnings(network);
  const symbol = getSymbol(network);

  setHistory(prev => [...prev, `Mining ${symbol} on ${network.toUpperCase()}...`]);

  miningIntervals[network] = setInterval(() => {
    let rate: number

    if (network === 'solana') {
      rate = Math.random() * (solMaxRatePerSecond - solMinRatePerSecond) + solMinRatePerSecond;
    } else {
      rate = Math.random() * (maxRatePerSecond - minRatePerSecond) + minRatePerSecond;
    }

    earnings += rate;
    setEarnings(network, earnings);

    const profit = rate.toFixed(5);
    const total = earnings.toFixed(4);
    const messages = generateMiningMessage(profit, total, symbol, network);

    setHistory(prev => [...prev, ...messages]);
  }, 3000);

  return [];
};

const stopMining = (
  setHistory: React.Dispatch<React.SetStateAction<string[]>>,
  network: string
): string[] => {
  const interval = miningIntervals[network];
  const symbol = getSymbol(network);
  const earnings = getEarnings(network);

  if (!interval) {
    return ['⚠ No mining in progress.'];
  }

  clearInterval(interval);
  miningIntervals[network] = null;

  setHistory(prev => [...prev, `Mining stopped. Final earnings: ${earnings.toFixed(4)} ${symbol}.`]);
  return [];
};

const checkEarnings = (network: string): string[] => {
  const earnings = getEarnings(network);
  const symbol = getSymbol(network);
  return [`Current ${symbol} earnings: ${earnings.toFixed(4)} ${symbol}.`];
};

const resetEarnings = (network: string): string[] => {
  setEarnings(network, 0);
  const symbol = getSymbol(network);
  return [`${symbol} earnings reset to 0.`];
};

export { startMining, stopMining, checkEarnings, resetEarnings, getEarnings };
